import { sendRequest } from "@infrastructure/api/api";
import type ApiResponse from "@core/types/apiResponse";
import type { ApiMessageResponse } from "@core/types/apiMessageResponse";

export type DepartmentReportScope = "church" | "district" | "nation";

export type PendingDepartmentReportUpload = {
  file: File;
  department_report_document_id?: number | null;
  description?: string | null;
};

export type ExistingDepartmentReportMediaPatch = {
  media_storage_id: number;
  description?: string | null;
};

export interface RequestDepartmentReportSavePayload {
  scope: DepartmentReportScope;


  churchId?: number;
  districtId?: number;
  nationId?: number;

  departmentId: number;
  reportTagId: number;

  notes: string | null;
  attachments?: PendingDepartmentReportUpload[];
  existingMedia?: ExistingDepartmentReportMediaPatch[];
}

export type RequestDepartmentReportSaveResponse = ApiMessageResponse & {
  [k: string]: unknown;
};

function buildBasePath(scope: DepartmentReportScope, ids: RequestDepartmentReportSavePayload): string {
  if (scope === "church") {
    if (!ids.churchId) throw new Error("churchId is required for scope=church");
    return `/church/${ids.churchId}`;
  }

  if (scope === "district") {
    if (!ids.districtId) throw new Error("districtId is required for scope=district");
    return `/district/${ids.districtId}`;
  }

  // nation
  const nationId = ids.nationId;
  if (!nationId) throw new Error("nationId is required for scope=nation");
  return `/nation/${nationId}`;
}

export function appendDepartmentReportMediaFormData(
  body: FormData,
  attachments?: PendingDepartmentReportUpload[],
  existingMedia?: ExistingDepartmentReportMediaPatch[]
): void {
  attachments?.forEach((item, index) => {
    body.append(`attachments[${index}][file]`, item.file);
    if (item.department_report_document_id != null) {
      body.append(
        `attachments[${index}][department_report_document_id]`,
        String(item.department_report_document_id)
      );
    }
    if (item.description != null && item.description !== "") {
      body.append(`attachments[${index}][description]`, item.description);
    }
  });
  existingMedia?.forEach((item, index) => {
    body.append(`existing_media[${index}][media_storage_id]`, String(item.media_storage_id));
    body.append(`existing_media[${index}][description]`, item.description ?? "");
  });
}

/**
 * Save department report notes (creates or updates a department_submission and sets status to DRAFT).
 * Expected endpoint: /{scopeBase}/department/{departmentId}/report/save
 */
export async function requestDepartmentReportSaveAsync(
  payload: RequestDepartmentReportSavePayload
): Promise<ApiResponse<RequestDepartmentReportSaveResponse>> {
  const base = buildBasePath(payload.scope, payload);

  const url = `${base}/department/${payload.departmentId}/report/save`;

  const body = new FormData();
  body.append("report_tag_id", String(payload.reportTagId));
  if (payload.notes != null) body.append("notes", payload.notes);
  appendDepartmentReportMediaFormData(body, payload.attachments, payload.existingMedia);

  return sendRequest<FormData, RequestDepartmentReportSaveResponse>("POST", url, body);
}